// 채팅 첨부 파일(업로드·붙여넣기·드래그&드롭·다운로드) + 드라이브 폴더 첨부 상태/로직(독립).
import { useState, useRef } from 'react';
import { chatAPI } from '../../../api/client';
import { alertDialog } from '../../../components/AppDialog';

const MAX_FILES = 20;     // 서버 file_service.MAX_FILES_PER_MESSAGE 와 같은 값
const MAX_FOLDERS = 3;    // 서버 chat_drive_folders.MAX_FOLDERS_PER_MESSAGE 와 같은 값

export default function useAttachments() {
  const [pendingFiles, setPendingFiles]   = useState([]);   // { file_id, name, preview }
  // 드라이브 폴더 첨부 — 경로만 들고 있다가 전송 때 drive_folders 로 보낸다(파일은 서버가 읽는다).
  const [pendingFolders, setPendingFolders] = useState([]); // { path, name, file_count, truncated }
  const [dragOver, setDragOver]           = useState(false); // 입력창 파일 드래그&드롭
  const fileRef = useRef(null);

  const addDriveFolder = (info) => {
    if (!info?.path) return;
    if (pendingFolders.some((f) => f.path === info.path)) return;
    if (pendingFolders.length >= MAX_FOLDERS) {
      alertDialog(`폴더는 메시지당 최대 ${MAX_FOLDERS}개까지 첨부할 수 있습니다.`);
      return;
    }
    const folder = {
      path: info.path,
      name: info.name || info.path.split('/').pop(),
      file_count: Number(info.file_count) || 0,
      truncated: !!info.truncated,
    };
    setPendingFolders((prev) => (prev.some((f) => f.path === folder.path) ? prev : [...prev, folder]));
  };

  const removeDriveFolder = (path) => {
    setPendingFolders((prev) => prev.filter((f) => f.path !== path));
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;

    const available = MAX_FILES - pendingFiles.length;
    if (available <= 0) {
      alertDialog(`파일은 최대 ${MAX_FILES}개까지 첨부할 수 있습니다.`);
      return;
    }
    const toUpload = files.slice(0, available);
    if (files.length > available) {
      alertDialog(`${files.length - available}개 파일이 한도 초과로 제외됩니다. (최대 ${MAX_FILES}개)`);
    }

    for (const file of toUpload) {
      const isImage = file.type.startsWith('image/');
      const limit = isImage ? 5 * 1024 * 1024 : 20 * 1024 * 1024;
      const limitLabel = isImage ? '5MB' : '20MB';
      if (file.size > limit) {
        alertDialog(`${file.name}: ${isImage ? '이미지' : '문서'} 파일은 ${limitLabel}를 초과할 수 없습니다.`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const r = await chatAPI.uploadFile(formData);
        const preview = isImage ? URL.createObjectURL(file) : null;   // 이미지면 로컬 미리보기 URL
        setPendingFiles((prev) => [...prev, { file_id: r.data.file_id, name: r.data.original_name, preview }]);
      } catch (err) {
        const msg = err.response?.data?.detail || '파일 업로드에 실패했습니다.';
        alertDialog(`${file.name}: ${msg}`);
      }
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    await uploadFiles(files);
  };

  // 클립보드 붙여넣기로 파일 첨부: 입력창에 Ctrl+V 시 클립보드에 파일(이미지·문서 등)이 있으면 첨부 업로드.
  // 용량·개수 제한은 일반 첨부(📎)와 동일하게 uploadFiles에서 처리.
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind !== 'file') continue;   // 텍스트 붙여넣기는 건드리지 않음
      const f = it.getAsFile();
      if (!f) continue;
      const isImage = (f.type || '').startsWith('image/');
      // 캡처 이미지는 이름이 없으므로(image.png) 식별 가능한 이름 부여, 그 외 파일은 원래 이름 유지
      if (isImage && (!f.name || f.name === 'image.png')) {
        const ext = (f.type.split('/')[1] || 'png').split('+')[0];
        files.push(new File([f], `clipboard_${Date.now()}.${ext}`, { type: f.type }));
      } else {
        files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();   // 파일이 있을 때만 기본 붙여넣기 막음(텍스트 붙여넣기는 영향 없음)
      await uploadFiles(files);
    }
  };

  // 내가 보낸 첨부 파일 다운로드(30일 경과 시 410 → 안내)
  const downloadFile = async (f) => {
    if (f.expired) { alertDialog('보관 기간(30일)이 지나 다운로드할 수 없습니다.'); return; }
    try {
      const res = await chatAPI.downloadUpload(f.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = f.name || 'download';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 410) alertDialog('보관 기간(30일)이 지나 다운로드할 수 없습니다.');
      else if (err.response?.status === 404) alertDialog('파일을 찾을 수 없습니다.');
      else alertDialog('다운로드에 실패했습니다.');
    }
  };

  return {
    pendingFiles, setPendingFiles, dragOver, setDragOver, fileRef, MAX_FILES, uploadFiles, handleFileChange, handlePaste, downloadFile,
    pendingFolders, setPendingFolders, addDriveFolder, removeDriveFolder, MAX_FOLDERS,
  };
}
